import moment from "moment";
import { DEFAULT_BRAND_PERFORMANCE_DATE_PARAMS, DEFAULT_BRAND_PERFORMANCE_PARAMETERS } from "../constants";
import { apiFetchV2, fetchDataV2 } from "./utils";

export const getLatestDataRefreshMetric = (metric: string) => {
  // use general parameters to get the brand_kpi metric needed for the brand_summary page
  const parameters = new URLSearchParams(window.location.search);
  const paramObj = Object.fromEntries(parameters.entries());
  const brandKPIParam = paramObj?.brand_kpi?.split(",");

  switch (metric) {
    case "image-attributes":
    case "image-snapshot":
      return "image";
    case "segment-volume-tiv":
    case "brand-market-share":
    case "monthly-table-for-download":
      return "Volume";
    case "market-share-by-channel":
      return "Volume By Channel";
    case "brand-kpi-summary":
      return `${brandKPIParam}`;
    case "rv":
    case "rv-table-for-download":
      return "Residual Value";
    case "nim":
    case "nim-latest":
      return "nim";
    default:
      return metric;
  }
};

export const getMarketParam = (market: string) => {
  // check if market has all and remove that and send empty string
  return /all/gi.test(market) ? `${DEFAULT_BRAND_PERFORMANCE_PARAMETERS?.market}` : market;
};

const getStartEndDateOfQuarter = (quarter: string, isChina?: boolean) => {
  if (isChina) {
    if (quarter?.includes("Q2")) return { startDate: "1 Apr", endDate: "30 Jun" };
    if (quarter?.includes("Q3")) return { startDate: "1 Jul", endDate: "30 Sep" };
    if (quarter?.includes("Q4")) return { startDate: "1 Oct", endDate: "31 Dec" };
    if (quarter?.includes("Q1")) return { startDate: "1 Jan", endDate: "31 Mar" };
  } else {
    if (quarter?.includes("Q1")) return { startDate: "1 Apr", endDate: "30 Jun" };
    if (quarter?.includes("Q2")) return { startDate: "1 Jul", endDate: "30 Sep" };
    if (quarter?.includes("Q3")) return { startDate: "1 Oct", endDate: "31 Dec" };
    if (quarter?.includes("Q4")) return { startDate: "1 Jan", endDate: "31 Mar" };
  }
  return { startDate: "", endDate: "" };
};

const getEarliestAndLatestDate = (dates: string[], isChina?: boolean) => {
  const quarterSortOrder = ["Q1", "Q2", "Q3", "Q4"];

  const availableYears = Array.from(new Set(dates?.map((date) => parseInt(date.split(" ")[2], 10))));
  const earliestYear = Math.min(...availableYears);
  const latestYear = Math.max(...availableYears);
  const earliestQuarter = dates
    ?.filter((date) => date?.includes(earliestYear.toString()))
    .sort((a, b) => quarterSortOrder?.indexOf(a) - quarterSortOrder?.indexOf(b))[0];

  const latestQuarter = dates
    ?.filter((date) => date?.includes(latestYear.toString()))
    .sort((a, b) => quarterSortOrder?.indexOf(a) - quarterSortOrder?.indexOf(b))
    .slice(-1)[0];

  const { startDate: startDateEarliest } = getStartEndDateOfQuarter(earliestQuarter, isChina);
  const { endDate: endDatelatest } = getStartEndDateOfQuarter(latestQuarter, isChina);

  if (latestQuarter?.includes("Q4") && earliestQuarter?.includes("Q4")) {
    return {
      startDate: `${startDateEarliest} ${earliestYear + 1}`,
      endDate: `${endDatelatest} ${latestYear + 1}`,
    };
  }

  // if the latest quarter is Q4, then the year should be the next year
  return {
    startDate: `${startDateEarliest} ${earliestYear}`,
    endDate: `${endDatelatest} ${dates?.length > 1 && !isChina && latestQuarter?.includes("Q4") ? latestYear + 1 : latestYear}`,
  };
};

// the optional metric is used for the brand KPI trend page that needs additional data
export const brandPerformanceApiParamsToObject = (params: string, metric?: string) => {
  const parameters = new URLSearchParams(window.location.search);
  // const paramsArgs = new URLSearchParams(params);

  // const parameters = new URLSearchParams(params);
  const paramObj = Object.fromEntries(parameters.entries());

  // filepath: /home/glenn/Desktop/job/nissan-cedar-frontend/src/api/brandPerformanceApi.ts
  const safeSplit = (value?: string) => (value ? value.split(",").map(decodeURIComponent) : []);

  // To Do - remove the regex and just to a direct check for all. e.g. if (paramObj?.model == "All") {}
  const dateRangeParam = parameters?.get("date_range");
  const brandParam = paramObj?.brand?.split(",");
  const modelParameter = /all/gi.test(paramObj?.model) ? [] : paramObj?.model?.split(",");
  const marketParam = /all/gi.test(paramObj?.market) ? [] : paramObj?.market?.split(",");
  const subCategoryParam = /all/gi.test(paramObj?.sub_category) ? [] : paramObj?.sub_category?.split(",");
  const annualMileageParam = /all/gi.test(paramObj?.annual_mileage) ? [] : paramObj?.annual_mileage?.split(",");
  const foreactMonthParam = /all/gi.test(paramObj?.mo) ? [] : paramObj?.mo?.split(",");
  const modelYearParam = /all/gi.test(paramObj?.model_year) ? [] : paramObj?.model_year?.split(",");

  // removed the split to all check on the entire string

  const attributeParam = /^all$/i.test(paramObj?.attribute) ? [] : safeSplit(paramObj?.attribute);
  const categoryParam = /all/gi.test(paramObj?.category) ? [] : paramObj?.category?.split(",");
  const competitorParam = /all/gi.test(paramObj?.competitor) ? [] : paramObj?.competitor?.split(",");
  const segmentParam = /all/gi.test(paramObj?.segment) ? [] : paramObj?.segment?.split(",");
  const dateRangeBrandPerf = parameters?.get("date_range_brand_perf")?.split(",") ?? [];
  const performancePageParam = parameters?.get("performance_page") ?? "volume";
  const granularityParam = parameters?.get("granularity") ?? "monthly";
  const brandKPIParam = paramObj?.brand_kpi?.split(",");
  const dataCategoryParam = /all/gi.test(paramObj?.data_category) ? [] : paramObj?.data_category?.split(",");
  const selectValueParam = paramObj?.select_value?.split(",");
  const rankingParam = parameters?.get("ranking");

  let date: string | Array<string> = dateRangeParam
    ? ["Last 6 quarters", ...DEFAULT_BRAND_PERFORMANCE_DATE_PARAMS].includes(dateRangeParam)
      ? dateRangeParam == "Last 12 months"
        ? "last_year"
        : dateRangeParam.split(" ").join("_").toLocaleLowerCase()
      : "custom"
    : "last_quarter";

  if (
    performancePageParam == "oao" ||
    performancePageParam == "image-attributes" ||
    performancePageParam == "image-snapshot" ||
    performancePageParam == "market-share-by-channel" ||
    performancePageParam == "brand-kpi-trend" ||
    performancePageParam == "brand-health-index" ||
    performancePageParam == "nim" ||
    performancePageParam == "nim-latest" ||
    performancePageParam == "purchase-funnel" ||
    performancePageParam == "rv" ||
    performancePageParam == "monthly-table-for-download" ||
    performancePageParam == "yearly-table-for-download" ||
    performancePageParam == "quarterly-table-for-download" ||
    performancePageParam == "rv-table-for-download"
  ) {
    if (dateRangeBrandPerf.includes("Last 6 quarters")) {
      date = "last_6_quarters";
    } else if (dateRangeBrandPerf.includes("Last 12 months")) {
      date = "last_year";
    } else {
      date = dateRangeBrandPerf;
    }
  } else {
    if (date == "custom") {
      paramObj["start_date"] = dateRangeParam
        ? moment(dateRangeParam.split("-")[0].trim(), "MMMM YYYY").startOf("month").format("YYYY-MM-DD")
        : "";
      paramObj["end_date"] = dateRangeParam
        ? moment(dateRangeParam.split("-")[1].trim(), "MMMM YYYY").endOf("month").format("YYYY-MM-DD")
        : "";
    }
  }

  // the brandKPISummary page has different parameters backend to work on this
  const brandKPISummaryParams = {
    market: [],
    model: ["CORPORATE"],
    sub_category: [],
  };

  const KPITrendParams = {
    model: ["CORPORATE"],
  };
  // hard code NIM to always send model and corporate - request from backend
  const NIMParams = {
    model: ["CORPORATE"],
  };

  delete paramObj?.model;
  delete paramObj?.market;
  delete paramObj?.brand;
  delete paramObj?.competitor;
  delete paramObj?.date_range;
  delete paramObj?.sub_category;
  delete paramObj?.attribute;
  delete paramObj?.category;
  delete paramObj?.performance_page;
  delete paramObj?.date_range_brand_perf;
  delete paramObj?.nissan_competitor;
  delete paramObj?.granularity;
  delete paramObj?.segment;
  delete paramObj?.brand_kpi;
  delete paramObj?.metric;
  delete paramObj?.data_category;
  delete paramObj?.select_value;
  delete paramObj?.ranking;
  delete paramObj?.forecast_month;
  delete paramObj?.mo;
  delete paramObj?.annual_mileage;

  return {
    ...paramObj,
    date_range: date,
    model: modelParameter,
    market: marketParam,
    brand: brandParam,
    competitor: competitorParam,
    sub_category: subCategoryParam,
    attribute: attributeParam,
    category: categoryParam,
    segment: segmentParam,
    brand_kpi: brandKPIParam,
    data_category: dataCategoryParam,
    select_value: selectValueParam,
    ranking: rankingParam,
    forecast_month: foreactMonthParam,
    model_year: modelYearParam,
    annual_mileage: annualMileageParam,
    granularity: granularityParam == undefined ? "monthly" : granularityParam,
    ...(performancePageParam === "brand-kpi-summary" && brandKPISummaryParams),
    ...(performancePageParam === "brand-kpi-trend" && KPITrendParams),
    ...(performancePageParam === "nim" && NIMParams),
  };
};

export const getNissanCompetitorParam = (params: string): boolean => {
  const parameters = new URLSearchParams(params);
  const paramObj = Object.fromEntries(parameters.entries());
  return paramObj?.nissan_competitor == "nissan_competitor";
};

export const getBrandVolumeTrend = (params: string) =>
  apiFetchV2(
    `/brand-performance/brand-volume/brand-volume-trend?use_cache=true&nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBrandVolumeAverage = (params: string) =>
  apiFetchV2(
    `/brand-performance/brand-volume/brand-volume-average?use_cache=true&nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBrandOaOTrend = (params: string) =>
  apiFetchV2(
    `/brand-performance/brand-oao/brand-oao-trend?use_cache=true&nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBrandOaOAverage = (params: string) =>
  apiFetchV2(
    `/brand-performance/brand-oao/brand-oao-average?use_cache=true&nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBrandHealthIndexTrend = (params: string) =>
  apiFetchV2(
    `/brand-performance/brand-health-index/brand-health-index-trend?use_cache=true&nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBrandHealthIndexAverage = (params: string) =>
  apiFetchV2(
    `/brand-performance/brand-health-index/brand-health-index-average?use_cache=true&nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBrandImageAttributesTrend = (params: string) =>
  apiFetchV2(
    `/brand-performance/brand-image-attributes/brand-image-attributes-trend?use_cache=true&nissan_competitor=${getNissanCompetitorParam(
      params
    )}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBrandImageAttributesAverage = (params: string) =>
  apiFetchV2(
    `/brand-performance/brand-image-attributes/brand-image-attributes-average?use_cache=true&nissan_competitor=${getNissanCompetitorParam(
      params
    )}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBuzzTrackingTrend = (params: string) =>
  apiFetchV2(
    `/brand-performance/buzz-tracking/buzz-tracking-trend?use_cache=true&nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBuzzTrackingAverage = (params: string) =>
  apiFetchV2(
    `/brand-performance/buzz-tracking/buzz-tracking-average?use_cache=true&nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBrandPerformanceSegmentVolume = (params: string) =>
  apiFetchV2(
    `/brand-performance/segment-volume-and-segment-tiv-share-volume/segment-volume-data?use_cache=true`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBrandPerformanceSegmentTIVShareVolume = (params: string) =>
  apiFetchV2(
    `/brand-performance/segment-volume-and-segment-tiv-share-volume/segment-tiv-share-volume-data?use_cache=true`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBrandMarketShareTrend = (params: string) =>
  apiFetchV2(
    `/brand-performance/brand-market-share/market-share-trend?use_cache=true&nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBrandMarketShareTable = (params: string) =>
  apiFetchV2(
    `/brand-performance/brand-market-share/market-share-table?use_cache=true&nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getMarketShareByChannelTrend = (params: string) =>
  apiFetchV2(
    `/brand-performance/market-share-by-channel/market-share-trend?use_cache=true&nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getMarketShareByChannelTable = (params: string) =>
  apiFetchV2(
    `/brand-performance/market-share-by-channel/market-share-table?use_cache=true&nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBrandKPITrend = (params: string) =>
  apiFetchV2(
    `/brand-performance/brand-kpi-trend/kpi-trend?use_cache=true&nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBrandKPISummary = (params: string) =>
  apiFetchV2(
    `/brand-performance/brand-kpi-summary/nissan-and-top-three-data?use_cache=true&nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getPurchaseFunnelData = (params: string) =>
  apiFetchV2(`/brand-performance/purchase-funnel/nissan-purchase-funnel-data`, brandPerformanceApiParamsToObject(params));

export const getAwarenessAndConsiderationByModel = (params: string) =>
  apiFetchV2(`/brand-performance/purchase-funnel/table-awareness-and-consideration-by-model`, brandPerformanceApiParamsToObject(params));

// Real backend now confirmed (NS-968) - see getTechAwarenessFilters' own comment in
// filtersApi.ts. Response shape is exactly what was predicted here: EurEpwrAwarenessOut is
// { series: List[dict], data: List[Dict[str, Optional[str]]] } (schemas/buy/
// brand_performance.py), matching TechAwarenessContent.tsx's own existing parsing unchanged.
export const getTechAwarenessData = (params: string) =>
  apiFetchV2(`/brand-performance/eur-epwr-awareness/awareness-by-powertrain`, brandPerformanceApiParamsToObject(params));

export const getBrandPerformanceDataDefinitions = () => fetchDataV2(`/brand-performance/data-definition`);

export const getSovSomTrend = (params: string) =>
  apiFetchV2(
    `/brand-performance/sov-som/trend?nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getSovSomTable = (params: string) =>
  apiFetchV2(
    `/brand-performance/sov-som/average-table?nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getImageSnapshotsBrandImageAttributes = (params: string) =>
  apiFetchV2(
    `/brand-performance/brand-image-snapshot/brand-image-attributes?nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getNIMTrend = (params: string) =>
  apiFetchV2(
    `/brand-performance/nim/trend?nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getNIMTable = (params: string) =>
  apiFetchV2(
    `/brand-performance/nim/average-table?nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getNIMLatestChartData = (params: string) =>
  apiFetchV2(
    `/brand-performance/nim-latest/chart-data?nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getNIMLatestTable = (params: string) =>
  apiFetchV2(
    `/brand-performance/nim-latest/table-data?nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getMonthlyTableForDownloadData = (params: string) =>
  apiFetchV2(
    `/brand-performance/monthly-table-for-download/data?nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getQuarterlyTableForDownloadData = (params: string) =>
  apiFetchV2(
    `/brand-performance/quarterly-table-for-download/data?nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getYearlyTableForDownloadData = (params: string) =>
  apiFetchV2(
    `/brand-performance/yearly-table-for-download/data?nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getRVTableForDownloadData = (params: string) =>
  apiFetchV2(
    `/brand-performance/rv-table-for-download/data?nissan_competitor=${getNissanCompetitorParam(params)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBrandPerformanceAvailableDataDateRange = (params: string, metric: string, market: string) =>
  fetchDataV2(
    `/brand-performance/get-available-data-date-range?metric=${getLatestDataRefreshMetric(metric)}&market=${getMarketParam(market)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBrandPerformancePurchaseFunnelAvailableDataDateRange = (params: string, metric: string, market: string) =>
  fetchDataV2(
    `/brand-performance/purchase-funnel/get-available-data-date-range?metric=${getLatestDataRefreshMetric(metric)}&market=${getMarketParam(
      market
    )}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getBrandPerformanceKPITrendAvailableDataDateRange = (params: string, metric: string, market: string) =>
  fetchDataV2(
    `/brand-performance/brand-kpi-trend/get-available-data-date-range?metric=${getLatestDataRefreshMetric(metric)}&market=${getMarketParam(
      market
    )}`,
    brandPerformanceApiParamsToObject(params)
  );

// Real backend now confirmed (NS-968, cedar-backend's ns-968-eur-epwr-awareness-master branch,
// commit 856018d1) - same dedicated-per-page pattern as purchase-funnel/brand-kpi-trend above,
// but with no `metric` query param: the route's own handler
// (get_eur_epwr_awareness_available_data_date_range) never accepts or forwards one - it calls
// EurEpwrAwarenessService(use_cache).get_available_data_date_range(market=market) directly, same
// as its own latest-refresh-date endpoint (getBrandPeformanceTechAwarenessLastRefreshDate,
// lastUpdatedDates.ts). The two functions above still send a `metric` query param to their own
// routes even though those handlers don't declare/use one either - FastAPI just silently ignores
// it - not copied here since it would be equally pointless.
export const getBrandPerformanceTechAwarenessAvailableDataDateRange = (params: string, market: string) =>
  fetchDataV2(
    `/brand-performance/eur-epwr-awareness/get-available-data-date-range?market=${getMarketParam(market)}`,
    brandPerformanceApiParamsToObject(params)
  );

export const getRVTrendData = (params: string) =>
  apiFetchV2(`/brand-performance/residaul-value/trend-data`, brandPerformanceApiParamsToObject(params));

export const getRVTableData = (params: string) =>
  apiFetchV2(`/brand-performance/residaul-value/table-data`, brandPerformanceApiParamsToObject(params));

export const getTPVATrendData = (params: string) =>
  apiFetchV2(`/brand-performance/tpva/trend-data`, brandPerformanceApiParamsToObject(params));

export const getTPVATableData = (params: string) =>
  apiFetchV2(`/brand-performance/tpva/table-data`, brandPerformanceApiParamsToObject(params));

export const getTPTableData = (params: string) => apiFetchV2(`/brand-performance/tp/table-data`, brandPerformanceApiParamsToObject(params));

export const getTPTrendData = (params: string) => apiFetchV2(`/brand-performance/tp/trend-data`, brandPerformanceApiParamsToObject(params));

export const getDataDictionary = (metric: string) => fetchDataV2(`/brand-performance/data-dictionary?metric=${metric}`);
